import React, { useEffect, useState } from 'react';
import { Button, Form, Input, message } from 'antd';
import { PhoneOutlined, MailOutlined, ClockCircleOutlined } from '@ant-design/icons';
import AppShell from './components/AppShell';
import Panel from './components/Panel';
import axiosInstance from './utils/axiosInstance';

/**
 * Only the support fields live here. Lucky spin protection moved to the 幸运转盘
 * page, next to the feature it governs. `updateSettings` patches just the keys it
 * receives, so the two forms save independently without overwriting each other.
 */
interface SettingsValues {
    supportPhone: string;
    supportEmail: string;
    liveChatHours: string;
}

const bar = 'h-3.5 rounded bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100 ' +
    'bg-[length:800px_100%] animate-shimmer';

/** Stands in for the fields while settings load, so inputs never flash empty. */
const FormSkeleton: React.FC = () => (
    <div className="animate-fade-in" aria-busy="true" aria-live="polite">
        {[0, 1, 2].map((i) => (
            <div key={i} className="mb-6 last:mb-0">
                <div className={`${bar} w-16`} style={{ animationDelay: `${i * 90}ms` }} />
                <div className="mt-2 h-8 rounded-md border border-line bg-slate-50" />
                <div
                    className={`${bar} mt-2 w-40 opacity-60`}
                    style={{ animationDelay: `${i * 90 + 45}ms` }}
                />
            </div>
        ))}
    </div>
);

const SettingsPage: React.FC = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);
    // Last values known to be on the server — what 重置 goes back to.
    const [saved, setSaved] = useState<SettingsValues>();

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await axiosInstance.post('/admin/protected/getSettings');
                const data = response?.data?.data;
                if (data) {
                    const values: SettingsValues = {
                        supportPhone: data.supportPhone || '',
                        supportEmail: data.supportEmail || '',
                        liveChatHours: data.liveChatHours || '',
                    };
                    setSaved(values);
                    form.setFieldsValue(values);
                }
            } catch (error) {
                console.error('Failed to fetch settings:', error);
                message.error('加载设置失败');
            } finally {
                setLoading(false);
            }
        };

        fetchSettings();
    }, [form]);

    const onFinish = async (values: SettingsValues) => {
        setSaving(true);
        try {
            await axiosInstance.post('/admin/protected/updateSettings', values);
            setSaved(values);
            setDirty(false);
            message.success('设置已保存');
        } catch (error) {
            console.error('Failed to update settings:', error);
            message.error('保存设置失败');
        } finally {
            setSaving(false);
        }
    };

    const reset = () => {
        if (saved) {
            form.setFieldsValue(saved);
        } else {
            form.resetFields();
        }
        setDirty(false);
    };

    return (
        <AppShell title="系统设置" subtitle="客服联系方式">
            <div className="mx-auto max-w-[560px]">
                <Panel>
                    <div className="-mx-4 -mt-4 mb-5 border-b border-line px-4 py-3.5">
                        <h2 className="m-0 text-[15px] font-semibold leading-tight text-ink">客服信息</h2>
                        <p className="m-0 mt-1 text-xs leading-tight text-muted">
                            展示在用户端的联系方式
                        </p>
                    </div>

                    <Form
                        form={form}
                        layout="vertical"
                        requiredMark={false}
                        onFinish={onFinish}
                        onValuesChange={() => setDirty(true)}
                    >
                        {loading ? <FormSkeleton /> : (
                            <>
                                <Form.Item
                                    name="supportPhone"
                                    label="客服电话"
                                    extra="用户点击拨打的号码"
                                    rules={[{ required: true, message: '请输入客服电话' }]}
                                >
                                    <Input
                                        prefix={<PhoneOutlined className="text-faint" />}
                                        placeholder="+65 1111 1118"
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="supportEmail"
                                    label="客服邮箱"
                                    extra="接收用户咨询的邮箱地址"
                                    rules={[
                                        { required: true, message: '请输入客服邮箱' },
                                        { type: 'email', message: '请输入有效的邮箱地址' },
                                    ]}
                                >
                                    <Input
                                        prefix={<MailOutlined className="text-faint" />}
                                        placeholder="support@bonia.com.sg"
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="liveChatHours"
                                    label="客服时间"
                                    extra="在线客服的服务时间"
                                >
                                    <Input
                                        prefix={<ClockCircleOutlined className="text-faint" />}
                                        placeholder="周一至周五 9:00 - 18:00"
                                    />
                                </Form.Item>

                            </>
                        )}

                        <div
                            className="-mx-4 -mb-4 mt-6 flex items-center justify-between gap-3
                                       border-t border-line bg-slate-50/60 px-4 py-3"
                        >
                            {/* Kept mounted so it fades rather than jumps, but hidden from
                                screen readers until there is actually something unsaved. */}
                            <span
                                aria-hidden={!dirty}
                                className={[
                                    'flex items-center gap-2 text-xs text-caution',
                                    'transition-opacity duration-200 ease-smooth',
                                    dirty ? 'opacity-100' : 'opacity-0',
                                ].join(' ')}
                            >
                                <span className="h-1.5 w-1.5 rounded-full bg-caution" />
                                未保存的更改
                            </span>

                            <div className="flex shrink-0 items-center gap-2">
                                <Button onClick={reset} disabled={loading || saving || !dirty}>
                                    重置
                                </Button>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={saving}
                                    disabled={loading || !dirty}
                                >
                                    保存设置
                                </Button>
                            </div>
                        </div>
                    </Form>
                </Panel>
            </div>
        </AppShell>
    );
};

export default SettingsPage;
